/**
 * Canonical helpers for fast + correct Supabase select typing.
 *
 * Why this exists
 * ---------------
 * `supabase-js` parses every `.select(sel("col1, col2, nested(name)"))` literal
 * at the type level to infer the row shape. On a schema with 500+ tables
 * and hundreds of query sites, that recursive template-literal parser
 * pushes `tsc`/`tsgo` from seconds to a hard 180s timeout.
 *
 * Pattern
 * -------
 *   import { sel, typedFrom } from "@/lib/supabase/select";
 *
 *   // 1. `sel(...)` widens the select to the cheap `*` result type so
 *   //    supabase-js skips parsing each large literal. Filter column names in
 *   //    `.eq/.in/.ilike` remain typed against the table row.
 *   const q = supabase
 *     .from("orders")
 *     .select(sel("id, order_number, customers(name)"))
 *     .eq("status", "paid");
 *
 *   // 2. Pin the exact row shape at the awaited edge with `.returns<T>()`.
 *   interface OrderRow {
 *     id: string;
 *     order_number: string;
 *     customers: { name: string } | null;
 *   }
 *   const { data, error } = await q.returns<OrderRow[]>();
 *
 *   // Or use the sugar wrapper:
 *   const { data } = await typedFrom<OrderRow[]>(
 *     supabase.from("orders").select(sel("id, order_number, customers(name)"))
 *   );
 *
 * Do NOT reintroduce a generic `<T extends string>(s: T): T` version — it
 * preserves the literal type and defeats the whole point of the helper.
 */

/**
 * Erase the literal type of a select string so supabase-js skips its
 * expensive template-literal parser, while using the cheap `*` result type so
 * callers that do not add `.returns<T>()` still get table-row correctness
 * instead of a `GenericStringError` collapse.
 *
 * Fast path: keeps typecheck under budget across 500+ tables.
 * Correctness path: use `.returns<T>()` (or `typedFrom<T>()`) at the
 * awaited edge to pin the exact row shape.
 */
// Return type is the literal `"*"` so supabase-js infers `Query = "*"` and
// hands back the full `Row` shape (not `GenericStringError`). The runtime
// value is still the original `s`, so PostgREST only projects the requested
// columns — TS just sees the wider row type, which is what callers want.
// Do NOT return `any` (widens to `GenericStringError` in the select union)
// or a generic `<T extends string>(s: T): T` (reintroduces the 180s
// literal-parse cost).
export const sel = (s: string): "*" => s as "*";

/**
 * Typed edge for a builder produced with `sel(...)`. Pins the row shape via
 * `.returns<T>()` so callers keep full result typing without paying the
 * literal-parse cost at every call site.
 */
export function typedFrom<T>(builder: { returns: <R>() => unknown }) {
  return (builder.returns as <R>() => PromiseLike<{ data: R | null; error: unknown }>)<T>();
}

/**
 * ---------------------------------------------------------------------------
 * GenericStringError type guards
 * ---------------------------------------------------------------------------
 *
 * When a `.select(...)` literal fails to parse (invalid column, ambiguous
 * relation, or a select the type parser cannot resolve), supabase-js
 * collapses the row type to `GenericStringError`:
 *
 *   type GenericStringError = { error: true } & string;
 *
 * That shape has no `.id`, `.name`, etc., so any downstream `row.id` triggers
 * TS2339 ("Property 'id' does not exist on type 'GenericStringError'").
 *
 * `isSupabaseError` / `unwrapRow` / `unwrapRows` narrow rows away from that
 * error shape at runtime AND at the type level so callers can safely read
 * real columns.
 */

/** Structural match for supabase-js's `GenericStringError`. */
export type GenericStringErrorShape = { error: true } & string;

/** Anything that is not the error collapse. */
export type NotSupabaseError<T> = T extends GenericStringErrorShape ? never : T;

/**
 * Runtime + type guard: `true` when supabase-js widened the row to
 * `GenericStringError`. Narrowing on the negative branch removes that arm
 * from the union, so `row.id` type-checks.
 *
 *   if (isSupabaseError(row)) continue;
 *   row.id; // OK
 */
export function isSupabaseError(value: unknown): value is GenericStringErrorShape {
  return (
    typeof value === "object" && value !== null && (value as { error?: unknown }).error === true
  );
}

/**
 * Return the row when it's a real row, otherwise `null`. Type is narrowed to
 * `T` (never `GenericStringError`).
 *
 *   const row = unwrapRow(data);
 *   if (row) console.log(row.id);
 */
export function unwrapRow<T>(value: T | null | undefined): NotSupabaseError<T> | null {
  if (value == null) return null;
  if (isSupabaseError(value)) return null;
  return value as NotSupabaseError<T>;
}

/**
 * Drop any `GenericStringError` entries from an array result. Type is
 * narrowed so `.map(r => r.id)` type-checks.
 *
 *   const rows = unwrapRows(data);
 *   rows.map(r => r.id);
 */
export function unwrapRows<T>(
  value: ReadonlyArray<T> | null | undefined,
): Array<NotSupabaseError<T>> {
  if (!value) return [];
  const out: Array<NotSupabaseError<T>> = [];
  for (const row of value) {
    if (row != null && !isSupabaseError(row)) {
      out.push(row as NotSupabaseError<T>);
    }
  }
  return out;
}
